import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { fmtEuro, MONTHS_EL_SHORT } from "@/lib/format";
import { EyeOff, ChevronDown, ChevronRight } from "lucide-react";

const M = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];

/* Expenses excluded from the P&L («Μετράει» = off), shown in their own table below the P&L. */
export const PLIgnoredExpenses = ({ year }) => {
  const [open, setOpen] = useState(true);
  const { data } = useQuery({ queryKey: ["exp-ignored", year], queryFn: async () => (await api.get("/expenses/ignored-monthly", { params: { year } })).data });
  if (!data || !data.rows.length) return null;
  const cell = (v, cls = "") => <td className={`px-2 py-1.5 text-right tabular whitespace-nowrap ${cls}`}>{v ? fmtEuro(v) : <span className="text-[#D8D0C4]">—</span>}</td>;
  let lastReason = null;
  return (
    <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden" data-testid="pl-ignored">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-[#FAF8F4]" data-testid="pl-ignored-toggle">
        {open ? <ChevronDown className="h-4 w-4 text-[#C86235]" /> : <ChevronRight className="h-4 w-4 text-[#B9AE9A]" />}
        <EyeOff className="h-4 w-4 text-[#8A8577]" />
        <div className="flex-1">
          <h2 className="font-medium text-[#45423C]">Έξοδα εκτός P&L <span className="text-sm font-normal text-[#6F6A60]">— παραστατικά που δεν μετράνε ({data.docs})</span></h2>
          <p className="text-xs text-[#8A8577] mt-0.5">Εξαιρούνται λόγω τύπου παραστατικού, κατηγορίας, προμηθευτή ή μεμονωμένα. Δεν επηρεάζουν κανένα σύνολο παραπάνω.</p>
        </div>
        <div className="text-right"><div className="text-[10px] uppercase tracking-wider text-[#8A8577]">Σύνολο εκτός</div><div className="font-semibold tabular text-[#45423C]" data-testid="pl-ignored-total">{fmtEuro(data.total)}</div></div>
        <Link to="/expense-categories?status=ignored" onClick={(e) => e.stopPropagation()} data-testid="pl-ignored-manage" className="ml-2 rounded-lg border border-[#E3DED4] px-3 py-1.5 text-xs font-semibold text-[#45423C] hover:border-[#45423C]">Διαχείριση →</Link>
      </button>
      {open && (
        <div className="overflow-x-auto border-t border-[#E3DED4]">
          <table className="w-full text-xs">
            <thead className="bg-[#F3F0EA] text-[10px] uppercase tracking-wider text-[#6F6A60]">
              <tr><th className="px-4 py-2 text-left min-w-[260px]">Λόγος / ομάδα</th><th className="px-2 py-2 text-right">Παρ.</th>{M.map((m) => <th key={m} className="px-2 py-2 text-right">{MONTHS_EL_SHORT[Number(m) - 1]}</th>)}<th className="px-3 py-2 text-right bg-[#EBE7DF]">Σύνολο</th></tr>
            </thead>
            <tbody>
              {data.rows.map((g, i) => {
                const header = g.reason !== lastReason; lastReason = g.reason;
                return (
                  <React.Fragment key={i}>
                    {header && <tr className="bg-[#FAF8F4]"><td colSpan={15} className="px-4 py-1.5 text-[10px] uppercase tracking-wider text-[#8A8577] font-semibold">{g.reason_label}</td></tr>}
                    <tr className="border-t border-[#F1EDE6] hover:bg-[#FBFAF7]" data-testid={`pl-ignored-row-${i}`}>
                      <td className="px-4 py-1.5 text-[#45423C] pl-7 max-w-[360px] truncate" title={g.label}>{g.label}</td>
                      <td className="px-2 py-1.5 text-right tabular text-[#8A8577]">{g.docs}</td>
                      {M.map((m) => <React.Fragment key={m}>{cell(g.months[m], g.months[m] < 0 ? "text-[#6E7A63]" : "text-[#6F6A60]")}</React.Fragment>)}
                      {cell(g.total, "font-semibold bg-[#F7F5F1]")}
                    </tr>
                  </React.Fragment>
                );
              })}
              <tr className="border-t-2 border-[#45423C] bg-[#F3F0EA] font-semibold" data-testid="pl-ignored-sum">
                <td className="px-4 py-2">Σύνολο εκτός P&L</td><td className="px-2 text-right tabular">{data.docs}</td>
                {M.map((m) => <React.Fragment key={m}>{cell(data.months[m])}</React.Fragment>)}
                {cell(data.total, "bg-[#EBE7DF]")}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
